import React, { createContext, FC, useContext, useEffect, useState } from "react";
import { cloneDeep, maxBy, orderBy } from "lodash";
import { BenchmarkingDashboardDetailsTypeEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters";
import { getPcaLineOfBusiness } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/utility";
import { BenchmarkingLineOfBusinessEnum, BenchmarkingNetworkType, BenchmarkingParentOrganizationType } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { MedicareNetwork } from "@app/overviews/network-benchmarking-dashboard/medicare/filters";
import { NetworkComparisonFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared";
import { QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters";
import {
  EffectivenessAndEfficiencyData,
  GhostData,
  HospitalAndPcpCountData,
  ProviderCountData,
  ProviderOverlapData,
  ProvidersByNetworkData,
  TotalProviderCountData,
} from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { EffectivenessEfficiencyValueEnum } from "@app/projects/servicing-provider";

export interface BenchmarkingContextValues {
  dashboardDetailsTotalCount: number;
  effectivenessAndEfficiencyData: EffectivenessAndEfficiencyData[];
  effectivenessAndEfficiencyDataLoading: boolean;
  getFormattedFiltersPayload: (payload: NetworkComparisonFiltersPayload) => NetworkComparisonFiltersPayload;
  ghostData: GhostData[];
  ghostDataLoading: boolean;
  hContractColorMap: Map<string, string>;
  hospitalAndPcpCountData: HospitalAndPcpCountData[];
  hospitalAndPcpCountDataLoading: boolean;
  onApplyNetworkComparisonFiltersClick: (networkComparisonFiltersPayload: NetworkComparisonFiltersPayload) => void;
  onDashboardDetailsViewClick: (
    drillinOption: BenchmarkingDashboardDetailsTypeEnum,
    clickedCount: number,
    selectedVersionId?: number,
    networkOverlappingCount?: number,
    isGhost?: boolean,
  ) => void;
  onSubmitNetworkFiltersClick: (parentOrganizations: BenchmarkingParentOrganizationType[]) => void;
  providerOverlapData: ProviderOverlapData;
  providerOverlapDataLoading: boolean;
  providersByNetworkData: ProvidersByNetworkData[];
  providersByNetworkDataLoading: boolean;
  setShowDashboardDetails: (showDashboardDetails: boolean) => void;
  showDashboardDetails: boolean;
  totalProviderCountData: TotalProviderCountData[];
  totalProviderCountDataLoading: boolean;
}

const initialValues: BenchmarkingContextValues = {
  dashboardDetailsTotalCount: null,
  effectivenessAndEfficiencyData: [],
  effectivenessAndEfficiencyDataLoading: false,
  getFormattedFiltersPayload: () => null,
  ghostData: [],
  ghostDataLoading: false,
  hContractColorMap: new Map<string, string>(),
  hospitalAndPcpCountData: [],
  hospitalAndPcpCountDataLoading: false,
  onApplyNetworkComparisonFiltersClick: () => null,
  onDashboardDetailsViewClick: () => null,
  onSubmitNetworkFiltersClick: () => null,
  providerOverlapData: null,
  providerOverlapDataLoading: false,
  providersByNetworkData: [],
  providersByNetworkDataLoading: false,
  setShowDashboardDetails: () => null,
  showDashboardDetails: false,
  totalProviderCountData: [],
  totalProviderCountDataLoading: false,
}

export const BenchmarkingContext = createContext(initialValues);

const BenchmarkingContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const [dashboardDetailsTotalCount, setDashboardDetailsTotalCount] = useState<number>(initialValues.dashboardDetailsTotalCount);
  const [effectivenessAndEfficiencyData, setEffectivenessAndEfficiencyData] = useState<EffectivenessAndEfficiencyData[]>(initialValues.effectivenessAndEfficiencyData);
  const [effectivenessAndEfficiencyDataLoading, setEffectivenessAndEfficiencyDataLoading] = useState<boolean>(initialValues.effectivenessAndEfficiencyDataLoading);
  const [ghostData, setGhostData] = useState<GhostData[]>(initialValues.ghostData);
  const [ghostDataLoading, setGhostDataLoading] = useState<boolean>(initialValues.ghostDataLoading);
  const [hContractColorMap, setHContractColorMap] = useState<Map<string, string>>(initialValues.hContractColorMap);
  const [hospitalAndPcpCountData, setHospitalAndPcpCountData] = useState<HospitalAndPcpCountData[]>(initialValues.hospitalAndPcpCountData);
  const [hospitalAndPcpCountDataLoading, setHospitalAndPcpCountDataLoading] = useState<boolean>(initialValues.hospitalAndPcpCountDataLoading);
  const [providerOverlapData, setProviderOverlapData] = useState<ProviderOverlapData>(initialValues.providerOverlapData);
  const [providerOverlapDataLoading, setProviderOverlapDataLoading] = useState<boolean>(initialValues.providerOverlapDataLoading);
  const [providersByNetworkData, setProvidersByNetworkData] = useState<ProvidersByNetworkData[]>(initialValues.providersByNetworkData);
  const [providersByNetworkDataLoading, setProvidersByNetworkDataLoading] = useState<boolean>(initialValues.providersByNetworkDataLoading);
  const [showDashboardDetails, setShowDashboardDetails] = useState<boolean>(initialValues.ghostDataLoading);
  const [totalProviderCountData, setTotalProviderCountData] = useState<TotalProviderCountData[]>(initialValues.totalProviderCountData);
  const [totalProviderCountDataLoading, setTotalProviderCountDataLoading] = useState<boolean>(initialValues.totalProviderCountDataLoading);
  const [unformattedGhostData, setUnformattedGhostData] = useState<ProvidersByNetworkData[]>([]);
  const [unformattedGhostDataLoading, setUnformattedGhostDataLoading] = useState<boolean>(false);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const networkComparisonFiltersContext = useContext(NetworkComparisonFiltersContext);

  useEffect(() => {
    if (!benchmarkingFiltersContext.displayedNetworkFilters.length || !benchmarkingFiltersContext.displayedPrimaryFilters) {
      setHospitalAndPcpCountData([]);
      setTotalProviderCountData([]);
      setEffectivenessAndEfficiencyData([]);
      setGhostData([]);
      setProviderOverlapData(null);
      setProvidersByNetworkData([]);
    }
  }, [benchmarkingFiltersContext.displayedNetworkFilters, benchmarkingFiltersContext.displayedPrimaryFilters]);

  useEffect(() => {
    if (!!providersByNetworkData.length && !providersByNetworkDataLoading && !!unformattedGhostData.length && !unformattedGhostDataLoading && !ghostData.length) {
      // ghost data is built when both the ghost data and providers by network data api calls are completed,
      // since the widget is to display percentages by dividing the ghost provider count by the providers by network count
      handleGhostData();
    }
  }, [
    providersByNetworkData,
    providersByNetworkDataLoading,
    unformattedGhostData,
    ghostDataLoading,
  ]);

  const getEffectivenessAndEfficiencyData = (networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): void => {
    setEffectivenessAndEfficiencyDataLoading(true);

    benchmarkingFiltersContext.getService().getEffectivenessAndEfficiencyData(networkComparisonFiltersPayload).then((data) => {
      if (!!data) {
        setEffectivenessAndEfficiencyData(data);
      }

      setEffectivenessAndEfficiencyDataLoading(false);
    });
  }

  const getGhostData = (networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): void => {
    setGhostData([]);
    setUnformattedGhostDataLoading(true);
    setGhostDataLoading(true);

    const buildZeroGhostData = (): GhostData[] => {
      const ghosts: GhostData[] = [];

      const allHContracts: BenchmarkingNetworkType[] = cloneDeep(networkComparisonFiltersPayload.comparisonNetworks);
      allHContracts.concat(networkComparisonFiltersPayload.primaryNetwork);

      allHContracts.forEach((contract) => {
        ghosts.push({
          contractId: contract.contractId,
          percentage: 0,
          providerCount: 0,
          versionId: contract.versionId,
        });
      });

      return ghosts;
    }

    if (!!networkComparisonFiltersPayload.volumeIndicators.length && !networkComparisonFiltersPayload.volumeIndicators.includes(ClaimsActivityVolumeLabelEnum.Ghost)) {
      // whenever ghost is not selected, all bars for each contract will be 0%
      setGhostData(buildZeroGhostData());
      setGhostDataLoading(false);
    } else {
      const payload: NetworkComparisonFiltersPayload = cloneDeep(networkComparisonFiltersPayload);
      payload.volumeIndicators = [ClaimsActivityVolumeLabelEnum.Ghost];

      benchmarkingFiltersContext.getService().getProvidersByNetworkData(payload).then((data) => {
        if (!!data) {
          setUnformattedGhostData(data);
        }

        setUnformattedGhostDataLoading(false);
      });
    }
  }

  const getHospitalAndPcpCountData = (networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): void => {
    setHospitalAndPcpCountDataLoading(true);

    benchmarkingFiltersContext.getService().getHospitalAndPcpCountData(networkComparisonFiltersPayload).then((data) => {
      if (!!data) {
        setHospitalAndPcpCountData(data);
      }

      setHospitalAndPcpCountDataLoading(false);
    });
  }

  const getProviderOverlapData = (networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): void => {
    setProviderOverlapDataLoading(true);

    // provider overlap data api call retrieves data for the following graphs:
    // 1.) provider overlap
    // 2.) provider count: in network
    // 3.) provider count: out of network
    benchmarkingFiltersContext.getService().getProviderOverlapData(networkComparisonFiltersPayload).then((data) => {
      if (!!data) {
        // unique bar is built off of in primary only count
        const uniqueCount: ProviderCountData = {
          count: data.inPrimaryOnlyCount,
          inNetwork: true,
          inOtherNetworkCount: "Unique",
        };

        data.inNetworkCounts = [uniqueCount, ...data.inNetworkCounts];
        setProviderOverlapData(data);
      }

      setProviderOverlapDataLoading(false);
    });
  }

  const getProvidersByNetworkData = (networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): void => {
    setProvidersByNetworkDataLoading(true);

    benchmarkingFiltersContext.getService().getProvidersByNetworkData(networkComparisonFiltersPayload).then((data) => {
      if (!!data) {
        setProvidersByNetworkData(data);
      }

      setProvidersByNetworkDataLoading(false);
    });
  }

  const getTotalProviderCountData = (networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): void => {
    setTotalProviderCountDataLoading(true);

    benchmarkingFiltersContext.getService().getTotalProviderCountData(networkComparisonFiltersPayload).then((data) => {
      if (!!data) {
        setTotalProviderCountData(data);
      }

      setTotalProviderCountDataLoading(false);
    });
  }

  const getFormattedFiltersPayload = (payload: NetworkComparisonFiltersPayload): NetworkComparisonFiltersPayload  => {
    // if all options are selected, send empty arrays in order to save resources
    if (payload.volumeIndicators.length === networkComparisonFiltersContext.mappedVolumeIndicators.length) {
      payload.volumeIndicators = [];
    }

    if (payload.effectivenessRanks.length === networkComparisonFiltersContext.mappedEffectivenessAndEfficiencyValues.length) {
      payload.effectivenessRanks = [];
    }

    if (payload.efficiencyRanks.length === networkComparisonFiltersContext.mappedEffectivenessAndEfficiencyValues.length) {
      payload.efficiencyRanks = [];
    }

    return payload;
  }

  const handleGhostData = (): void => {
    const formattedGhostData: GhostData[] = [];

    unformattedGhostData.forEach((datum) => {
      const providersByNetworkDatum: ProvidersByNetworkData = providersByNetworkData.find(x => x.contractId === datum.contractId);

      if (!!providersByNetworkDatum) {
        let percentage = 0;

        if (!!datum.providerCount && !!providersByNetworkDatum?.providerCount) {
          const unroundedPercentage = (datum.providerCount / providersByNetworkDatum.providerCount) * 100;
          const roundedPercentage = Math.round(unroundedPercentage * 10) / 10;

          // if there are values and the rounded percentage rounded to 0, display the unrounded percentage (example: 0.007%)
          percentage = roundedPercentage === 0 ? unroundedPercentage : roundedPercentage;
        }

        formattedGhostData.push({
          contractId: datum.contractId,
          percentage,
          providerCount: datum.providerCount,
          versionId: datum.versionId,
        });
      }
    });

    setGhostData(formattedGhostData);
    setGhostDataLoading(false);
    setUnformattedGhostData([]);
  }

  const onApplyNetworkComparisonFiltersClick = (networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): void => {
    networkComparisonFiltersPayload = getFormattedFiltersPayload(cloneDeep(networkComparisonFiltersPayload));

    getEffectivenessAndEfficiencyData(networkComparisonFiltersPayload);
    getGhostData(networkComparisonFiltersPayload);
    getProviderOverlapData(networkComparisonFiltersPayload);
    getProvidersByNetworkData(networkComparisonFiltersPayload);
  }

  const onDashboardDetailsViewClick = (
    drillinOption: BenchmarkingDashboardDetailsTypeEnum,
    clickedCount: number,
    selectedVersionId?: number,
    networkOverlappingCount?: number,
    isGhost?: boolean,
  ): void => {
    const filters: NetworkComparisonFiltersPayload = cloneDeep(networkComparisonFiltersContext.displayedNetworkComparisonFilters);
    filters.drillinOption = drillinOption;
    filters.selectedVersionId = selectedVersionId;
    filters.networkOverlappingCount = networkOverlappingCount;
    filters.lineOfBusiness = benchmarkingFiltersContext.selectedLineOfBusiness;

    if (isGhost) {
      filters.volumeIndicators = [ClaimsActivityVolumeLabelEnum.Ghost];
    }

    networkComparisonFiltersContext.setDisplayedNetworkComparisonFilters(filters);
    setShowDashboardDetails(true);
    setDashboardDetailsTotalCount(clickedCount);
  }

  const onSubmitNetworkFiltersClick = (parentOrganizations: BenchmarkingParentOrganizationType[]): void => {
    const selectedHContracts: BenchmarkingNetworkType[] = cloneDeep(benchmarkingFiltersContext.selectedNetworks);
    const hContractIds: string[] = selectedHContracts.map(x => x.contractId);
    const barColors: string[] = ["#402294", "#9E85E3", "#724CDE", "#C9BBEF", "#170057"];
    const newHContractColorMap: Map<string, string> = new Map<string, string>();

    hContractIds.forEach((id, index) => {
      newHContractColorMap.set(id, barColors[index % 5]);
    });

    // if any of your QES networks are selected, the primary network should default to your QES network with the highest membership count
    // if none of your QES networks are selected, the primary network should default to the network with the highest membership
    const yourNetworks: BenchmarkingNetworkType[] = parentOrganizations.find(x => x.parentOrganizationName === "Your QES Networks")?.hContracts;
    const selectedYourNetworks: BenchmarkingNetworkType[] = selectedHContracts.filter(y => yourNetworks.map(x => x.contractId).includes(y.contractId));
    const networks: BenchmarkingNetworkType[] = !!selectedYourNetworks.length ? selectedYourNetworks : selectedHContracts;
    let primaryNetwork: BenchmarkingNetworkType;

    switch (benchmarkingFiltersContext.displayedPrimaryFilters.lineOfBusinessId) {
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        primaryNetwork = orderBy(networks as QHPNetwork[], (x) => x.issuerId, "asc")[0];
        break;
      case BenchmarkingLineOfBusinessEnum.Medicare:
        primaryNetwork = maxBy(networks as MedicareNetwork[], (x) => x.membership);
        break;
      default:
        break;
    }

    const filteredHContracts: BenchmarkingNetworkType[] = selectedHContracts.filter(x => x.contractId !== primaryNetwork.contractId);

    const networkComparisonFiltersPayload: NetworkComparisonFiltersPayload = {
      comparisonNetworks: filteredHContracts,
      countyIds: benchmarkingFiltersContext.displayedPrimaryFilters.countyIds,
      effectivenessRanks: Object.values(EffectivenessEfficiencyValueEnum),
      efficiencyRanks: Object.values(EffectivenessEfficiencyValueEnum),
      pcaLineOfBusiness: getPcaLineOfBusiness(benchmarkingFiltersContext.selectedLineOfBusiness),
      primaryNetwork,
      specialtyGroupId: 0,
      volumeIndicators: Object.values(ClaimsActivityVolumeLabelEnum).filter(x => x !== ClaimsActivityVolumeLabelEnum.Total),
    };

    const formattedFiltersPayload: NetworkComparisonFiltersPayload = getFormattedFiltersPayload(cloneDeep(networkComparisonFiltersPayload));

    setHContractColorMap(newHContractColorMap);
    benchmarkingFiltersContext.setDisplayedNetworkFilters(selectedHContracts);
    networkComparisonFiltersContext.getNetworkComparisonFilterData(cloneDeep(networkComparisonFiltersPayload));
    getTotalProviderCountData(formattedFiltersPayload);
    getHospitalAndPcpCountData(formattedFiltersPayload);
    onApplyNetworkComparisonFiltersClick(formattedFiltersPayload);
  }

  return (
    <BenchmarkingContext.Provider
      value={{
        dashboardDetailsTotalCount,
        effectivenessAndEfficiencyData,
        effectivenessAndEfficiencyDataLoading,
        getFormattedFiltersPayload,
        ghostData,
        ghostDataLoading,
        hContractColorMap,
        hospitalAndPcpCountData,
        hospitalAndPcpCountDataLoading,
        onApplyNetworkComparisonFiltersClick,
        onDashboardDetailsViewClick,
        onSubmitNetworkFiltersClick,
        providerOverlapData,
        providerOverlapDataLoading,
        providersByNetworkData,
        providersByNetworkDataLoading,
        setShowDashboardDetails,
        showDashboardDetails,
        totalProviderCountData,
        totalProviderCountDataLoading,
      }}>
      {props.children}
    </BenchmarkingContext.Provider>
  )
}

export default BenchmarkingContextProvider;
